import { reactive } from 'vue'

/** chapter_type: null=全部，1=单词，2=句子 */
export const COURSE_CHAPTER_TYPE = {
  ALL: null,
  WORD: 1,
  SENTENCE: 2
}

export const COURSE_CHAPTER_TYPE_OPTIONS = [
  { value: null, label: '全部', desc: '单词与句子章节' },
  { value: 1, label: '单词', desc: '仅练习单词章节' },
  { value: 2, label: '句子', desc: '仅练习句子章节' }
]

/** 解析接口 type_list：[1] 单词、[2] 句子、[1,2] 均有 */
export function normalizeTypeList (raw) {
  if (!Array.isArray(raw)) return []
  return [...new Set(raw.map((n) => Number(n)).filter((n) => n === 1 || n === 2))].sort((a, b) => a - b)
}

/** 按课程所含类型生成弹窗选项 */
export function getChapterTypeOptionsForTypeList (typeList) {
  const types = normalizeTypeList(typeList)
  if (!types.length) return [...COURSE_CHAPTER_TYPE_OPTIONS]
  const opts = []
  if (types.length > 1) opts.push(COURSE_CHAPTER_TYPE_OPTIONS[0])
  if (types.includes(1)) opts.push(COURSE_CHAPTER_TYPE_OPTIONS.find((o) => o.value === 1))
  if (types.includes(2)) opts.push(COURSE_CHAPTER_TYPE_OPTIONS.find((o) => o.value === 2))
  return opts.filter(Boolean)
}

export function pickDefaultChapterType (typeList) {
  const types = normalizeTypeList(typeList)
  if (types.length === 1) return types[0]
  return null
}

/** 课程是否支持切换学习范围（同时含单词与句子） */
export function canSwitchChapterType (typeList) {
  return normalizeTypeList(typeList).length > 1
}

/** 按课程 type_list 校正 chapter_type（URL / 请求参数） */
export function sanitizeChapterTypeForCourse (chapterType, typeList) {
  const types = normalizeTypeList(typeList)
  const parsed = chapterType === 1 || chapterType === 2 ? chapterType : null
  if (!types.length) return parsed
  if (types.length === 1) return types[0]
  if (parsed && types.includes(parsed)) return parsed
  return null
}

export function courseDetailShowsWord (courseData) {
  const types = normalizeTypeList(courseData?.type_list)
  if (types.length && !types.includes(1)) return false
  return Number(courseData?.word_count) > 0
}

export function courseDetailShowsSentence (courseData) {
  const types = normalizeTypeList(courseData?.type_list)
  if (types.length && !types.includes(2)) return false
  return Number(courseData?.count) > 0
}

/**
 * 是否可建学习计划（plan_save 依赖可学习单词）。
 * type_list 仅 [2] 或 word_count≤0 时不可建，否则后端报「该课程暂无可学习单词」。
 */
export function courseSupportsLearningPlan (courseLike = {}) {
  const types = normalizeTypeList(courseLike.type_list ?? courseLike.typeList)
  const wordCount = Number(courseLike.word_count ?? courseLike.wordCount) || 0
  if (types.length && !types.includes(1)) return false
  return wordCount > 0
}

/** 与 plan_save 失败文案对齐，供前端拦截提示 */
export const LEARNING_PLAN_NO_WORDS_MSG = '该课程暂无可学习单词'

/** 列表项是否展示单词/句子统计（结合 type_list） */
export function courseItemShowsWord (item) {
  const types = normalizeTypeList(item?.type_list)
  if (types.length && !types.includes(1)) return false
  return Number(item?.word_count) > 0
}

export function courseItemShowsSentence (item) {
  const types = normalizeTypeList(item?.type_list)
  if (types.length && !types.includes(2)) return false
  return Number(item?.count) > 0
}

const state = reactive({
  visible: false,
  courseTitle: '',
  defaultType: null,
  typeList: [],
  _resolve: null
})

export function getCourseTypeSelectState () {
  return state
}

/**
 * 打开章节类型选择弹窗
 * @returns {Promise<number|null|undefined>} 选中类型；undefined 表示取消
 */
export function openCourseTypeSelect ({ title = '', defaultType = null, typeList = [] } = {}) {
  return new Promise((resolve) => {
    state.courseTitle = title
    state.typeList = normalizeTypeList(typeList)
    state.defaultType = defaultType ?? pickDefaultChapterType(state.typeList)
    state._resolve = resolve
    state.visible = true
  })
}

export function confirmCourseTypeSelect (chapterType) {
  state.visible = false
  const resolve = state._resolve
  state._resolve = null
  resolve?.(chapterType)
}

export function cancelCourseTypeSelect () {
  state.visible = false
  const resolve = state._resolve
  state._resolve = null
  resolve?.(undefined)
}

export function parseChapterType (raw) {
  if (raw === undefined || raw === null || raw === '') return null
  const n = Number(raw)
  return n === 1 || n === 2 ? n : null
}

/** question_list chapter_type：学习范围为单词/句子时返回 1/2，全部时返回 null（不传） */
export function resolveQuestionListChapterType ({ rangeChapterType = null } = {}) {
  return parseChapterType(rangeChapterType)
}

/** 游戏题型：全部时按章节 type，否则与学习范围一致 */
export function resolveGameSessionChapterType ({
  rangeChapterType = null,
  chapterItemType = null,
  courseType = null
} = {}) {
  const range = parseChapterType(rangeChapterType)
  if (range === 1 || range === 2) return range
  const itemType = parseChapterType(chapterItemType)
  if (itemType === 1 || itemType === 2) return itemType
  const parsedCourse = parseChapterType(courseType)
  if (parsedCourse === 1 || parsedCourse === 2) return parsedCourse
  return 2
}

export function getChapterTypeLabel (type) {
  const opt = COURSE_CHAPTER_TYPE_OPTIONS.find(o => o.value === type)
  return opt?.label || '全部'
}

export function buildCourseDetailLocation (courseId, chapterType = null) {
  const query = { id: String(courseId) }
  if (chapterType === 1 || chapterType === 2) {
    query.chapter_type = String(chapterType)
  }
  return { path: '/englishLearning/courseDetail', query }
}

/** 从列表进详情再返回时，保留列表页分页与滚动（keep-alive 场景） */
const LIST_ROUTES_PRESERVE_ON_BACK = new Set(['BatchEditing', 'MyCourse'])
const listPreservePending = Object.create(null)

export function markListPreserveOnBack (routeName) {
  if (routeName && LIST_ROUTES_PRESERVE_ON_BACK.has(routeName)) {
    listPreservePending[routeName] = true
  }
}

export function consumeListPreserveOnBack (routeName) {
  if (!listPreservePending[routeName]) return false
  delete listPreservePending[routeName]
  return true
}

/**
 * 从课程列表进入详情（不再弹前置学习范围选择；默认全部，仅一种类型时仍用该类型）
 * 学习范围切换请在课程详情页内手动操作。
 */
export async function navigateToCourseDetail (router, { id, title = '', typeList, preserveListOnBack = true } = {}) {
  if (!id) return false
  const types = normalizeTypeList(typeList)
  const fromRouteName = router.currentRoute.value?.name
  if (preserveListOnBack && fromRouteName) {
    markListPreserveOnBack(fromRouteName)
  }

  await router.push(buildCourseDetailLocation(id, pickDefaultChapterType(types)))
  return true
}
